/**
 * Deterministic validation — ARCHITECTURE.md section 6.4.
 *
 * NO LLM — pure deterministic code.
 * Validates extracted entities and relationships before they enter the graph.
 *
 * Four validation checks:
 * 1. Schema compliance: required fields present and correctly typed
 * 2. Completeness: percentage of non-null mandatory fields (threshold 60%)
 * 3. Plausibility: coordinates within desk geographic bounds, timestamps in window, confidence 0-1
 * 4. Grounding: extraction_quote verbatim in source text (already checked by extractor,
 *    re-verified here as defense in depth)
 */

import type { GeographicFocus } from '@/config/desk-config';
import type { ExtractedEntityWithProvenance } from './entity-extractor';
import type { IdentifiedRelationship } from './relationship-identifier';

// ---------------------------------------------------------------------------
// Validation result types
// ---------------------------------------------------------------------------

export interface EntityValidationResult {
  /** Entities that passed all validation checks */
  valid: ExtractedEntityWithProvenance[];
  /** Entities that failed validation, with reasons */
  rejected: Array<{ entity: ExtractedEntityWithProvenance; reasons: string[] }>;
  /** Whether any rejected entities are retryable (completeness below threshold) */
  retryable: boolean;
}

export interface RelationshipValidationResult {
  /** Relationships that passed all validation checks */
  valid: IdentifiedRelationship[];
  /** Relationships that failed validation, with reasons */
  rejected: Array<{ relationship: IdentifiedRelationship; reasons: string[] }>;
}

// ---------------------------------------------------------------------------
// Validation configuration
// ---------------------------------------------------------------------------

export interface ValidationConfig {
  /** Minimum percentage of mandatory fields that must be non-null (default: 0.6) */
  completenessThreshold?: number;
  /** Geographic bounds for plausibility checks */
  geographicFocus?: GeographicFocus;
  /** Temporal window for plausibility checks [start, end] as ISO 8601 */
  temporalBounds?: [string, string];
  /** Source text for grounding re-verification */
  sourceText?: string;
  /** Known entity names for relationship validation */
  knownEntityNames?: Set<string>;
}

// ---------------------------------------------------------------------------
// Entity type validation
// ---------------------------------------------------------------------------

const VALID_ENTITY_TYPES: Set<string> = new Set<string>([
  'PERSON', 'ORGANIZATION', 'LOCATION', 'ASSET', 'EVENT', 'INDICATOR', 'SOURCE',
]);

// ---------------------------------------------------------------------------
// Mandatory fields check
// ---------------------------------------------------------------------------

interface FieldCheck {
  name: string;
  present: boolean;
}

function checkMandatoryFields(entity: ExtractedEntityWithProvenance): FieldCheck[] {
  return [
    { name: 'type', present: !!entity.type },
    { name: 'name', present: !!entity.name && entity.name.trim().length > 0 },
    { name: 'provenance.sourceUrl', present: !!entity.provenance?.sourceUrl },
    { name: 'provenance.extractionQuote', present: !!entity.provenance?.extractionQuote },
    { name: 'provenance.collectorId', present: !!entity.provenance?.collectorId },
    { name: 'provenance.collectedAt', present: !!entity.provenance?.collectedAt },
    { name: 'provenance.confidence', present: typeof entity.provenance?.confidence === 'number' },
  ];
}

// ---------------------------------------------------------------------------
// Geographic plausibility
// ---------------------------------------------------------------------------

function isWithinBounds(
  lat: number,
  lng: number,
  focus: GeographicFocus,
): boolean {
  const [south, west, north, east] = focus.primary.coords;

  // Primary bbox check
  if (lat >= south && lat <= north && lng >= west && lng <= east) {
    return true;
  }

  // Secondary AOI check (radius-based)
  if (focus.secondary) {
    for (const aoi of focus.secondary) {
      const [centerLat, centerLng] = aoi.center;
      // Approximate distance check using equirectangular projection
      const dLat = (lat - centerLat) * 111.32; // km per degree latitude
      const dLng = (lng - centerLng) * 111.32 * Math.cos((centerLat * Math.PI) / 180);
      const distance = Math.sqrt(dLat * dLat + dLng * dLng);
      if (distance <= aoi.km) {
        return true;
      }
    }
  }

  return false;
}

// ---------------------------------------------------------------------------
// Timestamp plausibility
// ---------------------------------------------------------------------------

function isWithinTemporalBounds(
  timestamp: string,
  bounds: [string, string],
): boolean {
  try {
    const ts = new Date(timestamp).getTime();
    const start = new Date(bounds[0]).getTime();
    const end = new Date(bounds[1]).getTime();
    if (isNaN(ts) || isNaN(start) || isNaN(end)) return true; // Cannot validate — pass
    return ts >= start && ts <= end;
  } catch {
    return true; // Cannot parse — pass (don't reject on unparseable timestamps)
  }
}

// ---------------------------------------------------------------------------
// Grounding re-verification
// ---------------------------------------------------------------------------

function verifyGrounding(quote: string, sourceText: string): boolean {
  if (!quote || !sourceText) return false;
  const normalizeWs = (s: string) => s.replace(/\s+/g, ' ').trim();
  return normalizeWs(sourceText).toLowerCase().includes(normalizeWs(quote).toLowerCase());
}

// ---------------------------------------------------------------------------
// Entity validation
// ---------------------------------------------------------------------------

/**
 * Validate a batch of extracted entities using deterministic checks.
 * No LLM is used — all checks are pure code.
 */
export function validateEntities(
  entities: ExtractedEntityWithProvenance[],
  config: ValidationConfig = {},
): EntityValidationResult {
  const threshold = config.completenessThreshold ?? 0.6;
  const valid: ExtractedEntityWithProvenance[] = [];
  const rejected: Array<{ entity: ExtractedEntityWithProvenance; reasons: string[] }> = [];
  let hasRetryable = false;

  for (const entity of entities) {
    const reasons: string[] = [];

    // 1. Schema compliance: entity type must be valid
    if (!VALID_ENTITY_TYPES.has(entity.type)) {
      reasons.push(`Invalid entity type: "${entity.type}"`);
    }

    // 1. Schema compliance: name must be non-empty
    if (!entity.name || entity.name.trim().length === 0) {
      reasons.push('Entity name is empty');
    }

    // 1. Schema compliance: provenance must exist
    if (!entity.provenance) {
      reasons.push('Missing provenance object');
    }

    // 2. Completeness: check percentage of mandatory fields
    const fields = checkMandatoryFields(entity);
    const presentCount = fields.filter(f => f.present).length;
    const completeness = presentCount / fields.length;

    if (completeness < threshold) {
      const missing = fields.filter(f => !f.present).map(f => f.name);
      reasons.push(
        `Completeness ${(completeness * 100).toFixed(0)}% below threshold ${(threshold * 100).toFixed(0)}%: missing [${missing.join(', ')}]`,
      );
      hasRetryable = true;
    }

    // 3. Plausibility: confidence in valid range
    if (entity.provenance) {
      if (
        typeof entity.provenance.confidence !== 'number' ||
        entity.provenance.confidence < 0 ||
        entity.provenance.confidence > 1
      ) {
        reasons.push(
          `Confidence out of range: ${entity.provenance.confidence} (must be 0.0-1.0)`,
        );
      }
    }

    // 3. Plausibility: geographic bounds check for LOCATION entities
    if (entity.type === 'LOCATION' && config.geographicFocus) {
      const coords = entity.properties?.['coordinates'] as [number, number] | undefined;
      if (coords && Array.isArray(coords) && coords.length === 2) {
        const [lat, lng] = coords;
        if (typeof lat === 'number' && typeof lng === 'number') {
          if (!isWithinBounds(lat, lng, config.geographicFocus)) {
            reasons.push(
              `Location coordinates [${lat}, ${lng}] outside desk geographic bounds`,
            );
          }
        }
      }
    }

    // 3. Plausibility: temporal bounds check
    if (config.temporalBounds && entity.provenance?.collectedAt) {
      if (!isWithinTemporalBounds(entity.provenance.collectedAt, config.temporalBounds)) {
        reasons.push(
          `Collection timestamp ${entity.provenance.collectedAt} outside temporal bounds`,
        );
      }
    }

    // 4. Grounding: re-verify extraction quote exists in source text
    if (config.sourceText && entity.provenance?.extractionQuote) {
      if (!verifyGrounding(entity.provenance.extractionQuote, config.sourceText)) {
        reasons.push('Grounding re-check failed: extraction quote not found in source text');
      }
    }

    if (reasons.length > 0) {
      rejected.push({ entity, reasons });
    } else {
      valid.push(entity);
    }
  }

  return { valid, rejected, retryable: hasRetryable };
}

// ---------------------------------------------------------------------------
// Relationship validation
// ---------------------------------------------------------------------------

/**
 * Validate a batch of identified relationships using deterministic checks.
 * No LLM is used — all checks are pure code.
 */
export function validateRelationships(
  relationships: IdentifiedRelationship[],
  config: ValidationConfig = {},
): RelationshipValidationResult {
  const valid: IdentifiedRelationship[] = [];
  const rejected: Array<{ relationship: IdentifiedRelationship; reasons: string[] }> = [];

  for (const rel of relationships) {
    const reasons: string[] = [];

    // Required fields
    if (!rel.sourceEntity) reasons.push('Missing sourceEntity');
    if (!rel.targetEntity) reasons.push('Missing targetEntity');
    if (!rel.type) reasons.push('Missing relationship type');

    // Confidence range
    if (typeof rel.confidence !== 'number' || rel.confidence < 0 || rel.confidence > 1) {
      reasons.push(`Confidence out of range: ${rel.confidence} (must be 0.0-1.0)`);
    }

    // Evidence quote present
    if (!rel.evidenceQuote || rel.evidenceQuote.trim().length === 0) {
      reasons.push('Missing evidence quote');
    }

    // Self-referential check
    if (rel.sourceEntity && rel.targetEntity &&
        rel.sourceEntity.toLowerCase() === rel.targetEntity.toLowerCase()) {
      reasons.push('Self-referential relationship');
    }

    // Known entity check
    if (config.knownEntityNames) {
      if (!config.knownEntityNames.has(rel.sourceEntity.toLowerCase())) {
        reasons.push(`Source entity "${rel.sourceEntity}" not in known entity set`);
      }
      if (!config.knownEntityNames.has(rel.targetEntity.toLowerCase())) {
        reasons.push(`Target entity "${rel.targetEntity}" not in known entity set`);
      }
    }

    // Grounding re-check
    if (config.sourceText && rel.evidenceQuote) {
      if (!verifyGrounding(rel.evidenceQuote, config.sourceText)) {
        reasons.push('Grounding re-check failed: evidence quote not found in source text');
      }
    }

    if (reasons.length > 0) {
      rejected.push({ relationship: rel, reasons });
    } else {
      valid.push(rel);
    }
  }

  return { valid, rejected };
}
